import React, { useEffect, useState, useRef } from "react";
import { Plus, Pencil, Trash2, X, Upload, Star } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
  name: "", name_zh: "", description: "", description_zh: "", price: 0, category: "mains",
  image_url: "", is_veg: true, is_spicy: false, is_popular: false, is_special: false, is_available: true,
};

const CATEGORIES = ["soups", "starters", "specials", "mains", "biryani", "breads", "rice", "desserts", "drinks"];

export default function MenuAdmin({ specialsOnly = false }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const res = await api.get("/menu");
    setItems(specialsOnly ? res.data.filter((i) => i.is_special) : res.data);
  };

  useEffect(() => { load(); }, [specialsOnly]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, is_special: specialsOnly });
    setShowForm(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({ ...it });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, price: parseFloat(form.price) };
      if (editing) {
        await api.put(`/menu/${editing.id}`, payload);
        toast.success("Item updated");
      } else {
        await api.post("/menu", payload);
        toast.success("Item created");
      }
      setShowForm(false);
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/menu/${id}`);
    toast.success("Deleted");
    load();
  };

  const uploadImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const backend = process.env.REACT_APP_BACKEND_URL;
      setForm({ ...form, image_url: `${backend}${res.data.url}` });
      toast.success("Image uploaded");
    } catch (e) {
      toast.error("Upload failed");
    }
  };

  return (
    <div data-testid={specialsOnly ? "specials-admin" : "menu-admin"}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif-display text-4xl text-gold-gradient">
            {specialsOnly ? "Chef's Specials" : "Menu Management"}
          </h1>
          <p className="text-sm text-[#D1C7C9]">{specialsOnly ? "Curate signature dishes shown on home page" : "Add, edit and organize dishes"}</p>
        </div>
        <button onClick={openCreate} className="btn-saffron" data-testid="add-item-btn">
          <Plus size={14} /> Add {specialsOnly ? "Special" : "Item"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((it) => (
          <div key={it.id} className="bg-[#1A1A1A] border border-[#333] rounded-xl p-4 flex gap-4" data-testid={`admin-menu-item-${it.id}`}>
            {it.image_url ? (
              <img src={it.image_url} alt="" className="w-20 h-20 rounded-md object-cover" />
            ) : (
              <div className="w-20 h-20 rounded-md bg-[#2A1116] flex items-center justify-center text-[#D4AF37]/40">·</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-medium truncate">{it.name}</h3>
                {it.is_special && <Star size={12} className="text-[#D4AF37]" fill="currentColor" />}
              </div>
              <p className="text-xs text-[#D1C7C9] capitalize">{it.category} · NT$ {it.price}</p>
              <p className="text-xs text-[#D1C7C9]/60 line-clamp-2 mt-1">{it.description}</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => openEdit(it)} className="text-xs text-[#E69138] hover:text-[#F2A65A]" data-testid={`edit-${it.id}`}>
                  <Pencil size={12} className="inline" /> Edit
                </button>
                <button onClick={() => del(it.id)} className="text-xs text-red-400 hover:text-red-300" data-testid={`delete-${it.id}`}>
                  <Trash2 size={12} className="inline" /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-[#D1C7C9] col-span-full">No items yet.</p>}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-[#1A1A1A] border border-[#333] rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="menu-form-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif-display text-2xl text-gold-gradient">{editing ? "Edit Item" : "New Item"}</h3>
              <button type="button" onClick={() => setShowForm(false)}><X size={20} className="text-[#D1C7C9]" /></button>
            </div>
            <div className="space-y-3">
              <Input label="Name (English)" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testId="form-name" />
              <Input label="Name (中文)" value={form.name_zh} onChange={(v) => setForm({ ...form, name_zh: v })} testId="form-name-zh" />
              <TextArea label="Description (English)" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testId="form-desc" />
              <TextArea label="Description (中文)" value={form.description_zh} onChange={(v) => setForm({ ...form, description_zh: v })} testId="form-desc-zh" />
              <div className="grid grid-cols-2 gap-3">
                <Input label="Price (NT$)" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} required testId="form-price" />
                <Select label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={CATEGORIES} testId="form-category" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#D1C7C9] block mb-1">Image</label>
                {form.image_url && <img src={form.image_url} alt="" className="w-24 h-24 rounded-md object-cover mb-2" />}
                <div className="flex gap-2">
                  <input
                    value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    placeholder="Image URL"
                    className="flex-1 bg-[#0A0A0A] border border-[#333] rounded-md px-3 py-2 text-sm"
                    data-testid="form-image-url"
                  />
                  <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => uploadImage(e.target.files[0])} />
                  <button type="button" onClick={() => fileRef.current?.click()} className="btn-outline-gold text-xs px-3" data-testid="form-upload-img">
                    <Upload size={12} /> Upload
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Toggle label="Vegetarian" checked={form.is_veg} onChange={(v) => setForm({ ...form, is_veg: v })} testId="form-veg" />
                <Toggle label="Spicy" checked={form.is_spicy} onChange={(v) => setForm({ ...form, is_spicy: v })} testId="form-spicy" />
                <Toggle label="Popular" checked={form.is_popular} onChange={(v) => setForm({ ...form, is_popular: v })} testId="form-popular" />
                <Toggle label="Chef's Special" checked={form.is_special} onChange={(v) => setForm({ ...form, is_special: v })} testId="form-special" />
                <Toggle label="Available" checked={form.is_available} onChange={(v) => setForm({ ...form, is_available: v })} testId="form-available" />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button type="button" onClick={() => setShowForm(false)} className="btn-outline-gold flex-1 justify-center">Cancel</button>
              <button type="submit" className="btn-saffron flex-1 justify-center" data-testid="form-save-btn">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Input({ label, type = "text", value, onChange, required, testId }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-[#D1C7C9] block mb-1">{label}</span>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-[#0A0A0A] border border-[#333] rounded-md px-3 py-2 text-sm text-[#FDFBF7] outline-none focus:border-[#E69138]"
      />
    </label>
  );
}
function TextArea({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-[#D1C7C9] block mb-1">{label}</span>
      <textarea
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-[#0A0A0A] border border-[#333] rounded-md px-3 py-2 text-sm text-[#FDFBF7] outline-none focus:border-[#E69138] resize-none"
      />
    </label>
  );
}
function Select({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-[#D1C7C9] block mb-1">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full bg-[#0A0A0A] border border-[#333] rounded-md px-3 py-2 text-sm text-[#FDFBF7] outline-none focus:border-[#E69138] capitalize">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
function Toggle({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-[#0A0A0A]">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="accent-[#E69138]" data-testid={testId} />
      <span className="text-xs text-[#D1C7C9]">{label}</span>
    </label>
  );
}
